import type { SearchClient } from 'algoliasearch/lite'
import { createAlgoliaLiteClient } from '~/utils/createAlgoliaLiteClient'
import {
  getAlgoliaKeySourceLabel,
  resolveAlgoliaServerApiKeyWithSource,
} from '~/utils/resolveAlgoliaServerApiKey'

/**
 * Per-request SSR cache. Do not use useState — SearchClient contains functions
 * and cannot be serialized into the Nuxt payload (devalue error).
 */
interface NuxtAppWithAlgoliaSsr extends ReturnType<typeof useNuxtApp> {
  _algoliaSsrSearchClient?: SearchClient
}

const logAlgoliaKeyUsage = (runtime: 'client' | 'ssr', keyLabel: string) => {
  if (!import.meta.dev) {
    return
  }

  let route: string | undefined
  try {
    route = useRoute().fullPath
  } catch {
    // useRoute unavailable outside Nuxt setup / async context
  }

  console.log('[Algolia] search request', {
    runtime,
    key: keyLabel,
    ...(route ? { route } : {}),
  })
}

export const useAlgoliaSearchClient = (): SearchClient => {
  if (import.meta.client) {
    logAlgoliaKeyUsage('client', 'ALGOLIA_API_CLIENT_KEY (public)')
    return useNuxtApp().$algolia as SearchClient
  }

  const config = useRuntimeConfig()
  const { apiKey, keySource } = resolveAlgoliaServerApiKeyWithSource(config)
  logAlgoliaKeyUsage('ssr', getAlgoliaKeySourceLabel(keySource))

  const nuxtApp = useNuxtApp() as NuxtAppWithAlgoliaSsr
  if (nuxtApp._algoliaSsrSearchClient) {
    return nuxtApp._algoliaSsrSearchClient
  }

  const algoliaModuleConfig = config.public.algolia as
    | { applicationId?: string; useFetch?: boolean }
    | undefined

  const appId =
    algoliaModuleConfig?.applicationId || config.public.algoliaAppId || ''

  const client = createAlgoliaLiteClient({
    appId,
    apiKey,
    useFetch: algoliaModuleConfig?.useFetch ?? true,
  })

  nuxtApp._algoliaSsrSearchClient = client
  return client
}
